import React, { useCallback } from 'react'
import { USER_TYPE } from 'constants/Users'
import Input from 'components/common/input/Input'
import SelectComponent from 'components/common/select/SelectComponent'
import { ErrorMessage, useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import { useDropzone } from 'react-dropzone'
import { CloudArrowUp } from 'phosphor-react'
import { minioSingleFileUpload } from 'containers/events/Api'
import { toast } from 'react-toastify'
import CustomToast from 'components/common/custom-toast/CustomToast'
import { alertTypes } from 'constants/Common'

function UserCreateModal({
  handleCloseModal,
  selectedUser,
  handleChange,
  values,
  handleSubmit,
  teamsList,
  teamsLoading,
  organizationsList,
  currentOrganizationId,
  setFieldValue,
}) {
  const formik = useFormikContext()
  const isTypeSelected = Boolean(values.type)
  const isSpeaker = values.type === 'speaker'

  const organizationOptions = organizationsList
    .filter((item) => item.enabled !== false)
    .map((item) => ({
      value: String(item.organizationId || item.id || ''),
      label: item.org_name || item.name || item.organizationName,
      isCurrent:
        String(item.organizationId || item.id || '') ===
        String(currentOrganizationId || ''),
    }))
    .filter((item) => item.value && item.label)
    .sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent))
    .map(({ value, label, isCurrent }) => ({
      value,
      label: isCurrent ? `${label} ` : label,
    }))
  const selectedOrganizationOptions = organizationOptions.filter((item) =>
    (values.organizationIds || []).includes(item.value)
  )

  const submitButtonLabel = (() => {
    const type = values.type
    if (selectedUser) {
      if (type === 'speaker') return 'Update Speaker'
      if (type === 'admin') return 'Update Admin'
      return 'Update User'
    }
    if (type === 'speaker') return 'Add Speaker'
    if (type === 'admin') return 'Add Admin'
    return 'Add User'
  })()

  const handleFileUpload = useCallback(
    async (file) => {
      const formData = new FormData()
      formData.append('file', file, 'file')
      formData.append('project', 'xler')
      formData.append('fileType', file.type)

      const result = await minioSingleFileUpload(formData, () => {})
      if (result?.message && result?.paths) {
        setFieldValue('image', result.paths)
        toast(
          <CustomToast
            variant={alertTypes.SUCCESS}
            message={result?.message || 'Successfully!'}
          />
        )
        return
      }

      toast(
        <CustomToast
          variant={alertTypes.DANGER}
          message={result?.response?.data?.error || 'Upload failed'}
        />
      )
    },
    [setFieldValue]
  )

  const onDrop = useCallback(
    (files) => {
      const [first] = files || []
      if (first) handleFileUpload(first)
    },
    [handleFileUpload]
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled: !isTypeSelected,
    accept: {
      'image/png': [],
      'image/jpeg': [],
      'image/jpg': [],
    },
    maxSize: 10 * 1024 * 1024,
  })

  console.log('formik errors ', formik.errors)

  return (
    <form className='form-main user-create-form' onSubmit={handleSubmit}>
      <div className='row'>
        <div className='col-md-6'>
          <div className='field-wrapper'>
            <div className='select-field'>
              <label htmlFor='type' className='input-label'>
                Type *
              </label>
              <SelectComponent
                name='type'
                options={USER_TYPE}
                selectedValue={values.type}
                placeholder='Select'
                handleChange={(obj) => {
                  setFieldValue('type', obj.value)
                }}
              />
            </div>
            <ErrorMessage className='error-text' component='p' name='type' />
          </div>
        </div>

        {!isTypeSelected ? (
          <div className='col-md-12'>
            <p className='user-create-form__hint'>
              Select a type to enable the rest of the form.
            </p>
          </div>
        ) : null}

        {isTypeSelected && isSpeaker ? (
          <>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='firstName'
                  handleChange={handleChange}
                  placeholder='First Name'
                  label='First Name *'
                  value={values.firstName}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='firstName'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='lastName'
                  handleChange={handleChange}
                  placeholder='Last Name'
                  label='Last Name *'
                  value={values.lastName}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='lastName'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='email'
                  handleChange={handleChange}
                  placeholder='Email'
                  label='Email *'
                  value={values.email}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='email'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='password'
                  handleChange={handleChange}
                  placeholder={
                    selectedUser
                      ? 'Password'
                      : 'Password'
                  }
                  label={selectedUser ? 'Password' : 'Password *'}
                  type='password'
                  value={values.password}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='password'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='phone'
                  handleChange={handleChange}
                  placeholder='Phone Number'
                  label='Phone Number *'
                  value={values.phone}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='phone'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <div className='select-field'>
                  <label htmlFor='organizationIds' className='input-label'>
                    Organization *
                  </label>
                  <SelectComponent
                    name='organizationIds'
                    options={organizationOptions}
                    selectedValue={selectedOrganizationOptions}
                    placeholder='Select'
                    isMulti
                    handleChange={(options) =>
                      setFieldValue(
                        'organizationIds',
                        (options || []).map((item) => item.value)
                      )
                    }
                  />
                </div>
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='organizationIds'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='designation'
                  handleChange={handleChange}
                  placeholder='Speaker Designation'
                  label='Speaker Designation *'
                  value={values.designation}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='designation'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='description'
                  handleChange={(e) =>
                    setFieldValue('description', e.target.value)
                  }
                  placeholder='Speaker Description'
                  label='Speaker Description *'
                  value={values.description || ''}
                  type='textarea'
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='description'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <span className='input-label'>Speaker Image *</span>
                <div {...getRootProps()} className='speaker-image-upload'>
                  <input {...getInputProps()} />
                  <CloudArrowUp size={28} />
                  <p>{values.image ? 'Change Photo' : 'Choose Photo'}</p>
                </div>
                {values.image ? (
                  <div className='speaker-image-preview'>
                    <img src={values.image} alt='Speaker' />
                  </div>
                ) : null}
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='image'
                />
              </div>
            </div>
          </>
        ) : null}

        {isTypeSelected && !isSpeaker ? (
          <>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='firstName'
                  handleChange={handleChange}
                  placeholder='First Name'
                  label='First Name *'
                  value={values.firstName}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='firstName'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='lastName'
                  handleChange={handleChange}
                  placeholder='Last Name'
                  label='Last Name *'
                  value={values.lastName}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='lastName'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='name'
                  handleChange={handleChange}
                  placeholder='Preferred name for name tag'
                  label='Preferred name for name tag *'
                  value={values.name}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='name'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='email'
                  handleChange={handleChange}
                  placeholder='Email'
                  label='Email *'
                  value={values.email}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='email'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='password'
                  handleChange={handleChange}
                  placeholder={
                    selectedUser
                      ? 'Password'
                      : 'Password'
                  }
                  label={selectedUser ? 'Password' : 'Password *'}
                  type='password'
                  value={values.password}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='password'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='company'
                  handleChange={handleChange}
                  placeholder='Company'
                  label='Company (Optional)'
                  value={values.company}
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='jobTitle'
                  handleChange={handleChange}
                  placeholder='Job Title'
                  label='Job Title'
                  value={values.jobTitle}
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <div className='select-field'>
                  <label htmlFor='organizationIds' className='input-label'>
                    Organization *
                  </label>
                  <SelectComponent
                    name='organizationIds'
                    options={organizationOptions}
                    selectedValue={selectedOrganizationOptions}
                    placeholder='Select'
                    isMulti
                    handleChange={(options) =>
                      setFieldValue(
                        'organizationIds',
                        (options || []).map((item) => item.value)
                      )
                    }
                  />
                </div>
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='organizationIds'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <div className='select-field'>
                  <label htmlFor='team' className='input-label'>
                    Team
                  </label>
                  {teamsLoading ? (
                    <p className='mb-0'>Loading teams...</p>
                  ) : teamsList.length > 0 ? (
                    /* eslint no-underscore-dangle: 0 */
                    <SelectComponent
                      name='team'
                      options={teamsList.map((item) => ({
                        value: String(item._id || ''),
                        label:
                          item.name.charAt(0).toUpperCase() +
                          item.name.slice(1),
                      }))}
                      selectedValue={
                        values.teamId ? String(values.teamId) : null
                      }
                      placeholder='Select'
                      handleChange={(obj) =>
                        setFieldValue('teamId', obj?.value || '')
                      }
                    />
                  ) : (
                    <p className='mb-0 user-create-form__empty-hint'>
                      No team found
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='phone'
                  handleChange={handleChange}
                  placeholder='Phone'
                  label='Phone Number *'
                  value={values.phone}
                />
                <ErrorMessage
                  className='error-text'
                  component='p'
                  name='phone'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='workLocation'
                  handleChange={handleChange}
                  placeholder='Work Location'
                  label='Work Location'
                  value={values.workLocation}
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='emergencyContactName'
                  handleChange={handleChange}
                  placeholder='Emergency Contact Name'
                  label='Emergency Contact Name'
                  value={values.emergencyContactName}
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='emergencyContactPhoneNumber'
                  handleChange={handleChange}
                  placeholder='Emergency Contact Phone Number'
                  label='Emergency Contact Phone Number'
                  value={values.emergencyContactPhoneNumber}
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='emergencyContactRelationship'
                  handleChange={handleChange}
                  placeholder='Emergency Contact Relationship'
                  label='Emergency Contact Relationship'
                  value={values.emergencyContactRelationship}
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='managerName'
                  handleChange={handleChange}
                  placeholder='Your Manager Name'
                  label='Your Manager Name'
                  value={values.managerName}
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='field-wrapper'>
                <Input
                  name='managerPhoneNumber'
                  handleChange={handleChange}
                  placeholder='Your Manager Phone Number'
                  label='Your Manager Phone Number'
                  value={values.managerPhoneNumber}
                />
              </div>
            </div>
          </>
        ) : null}

        <div className='col-md-12 d-flex justify-content-end gap-4 mt-2'>
          <button
            type='button'
            className='secondary-btn record-btn'
            onClick={handleCloseModal}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='primary-btn record-btn'
            disabled={!isTypeSelected}
          >
            {submitButtonLabel}
          </button>
        </div>
      </div>
    </form>
  )
}

UserCreateModal.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  selectedUser: PropTypes.objectOf.isRequired,
  handleChange: PropTypes.func.isRequired,
  values: PropTypes.arrayOf.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  teamsList: PropTypes.arrayOf.isRequired,
  teamsLoading: PropTypes.bool,
  organizationsList: PropTypes.arrayOf.isRequired,
  currentOrganizationId: PropTypes.string.isRequired,
}

UserCreateModal.defaultProps = {
  teamsLoading: false,
}

export default UserCreateModal
